import { isLogicalFilter } from '@/sections/insights/queryBuilder/utils/filterTypeGuards';
import type { QueryFilter } from '@/types';

export const validateNormalizedFilters = (
  filters: QueryFilter[]
): { valid: boolean; reason?: string } => {
  for (const filter of filters) {
    var result = validateOutermostFilter(filter);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
};

const validateOutermostFilter = (
  filter: QueryFilter
): { valid: boolean; reason?: string } => {
  const result = isLogicalWithChildren(filter);
  if (!result.valid) {
    return {
      valid: false,
      reason: 'outer filter is invalid: ' + result.reason,
    };
  }

  for (const child of filter.value as QueryFilter[]) {
    const result = validateLeafFilter(child);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
};

const validateLeafFilter = (
  filter: QueryFilter
): { valid: boolean; reason?: string } => {
  // Allow one level of logical nesting — e.g. a SimpleFilterDefinition's
  // translateForAPI may return an OR/AND wrapper whose children are
  // primitive-valued leaf filters (used by RiskName to search both
  // Risk.name and Vulnerability.title simultaneously).
  if (isLogicalFilter(filter)) {
    const children = filter.value;
    if (!Array.isArray(children)) {
      return { valid: false, reason: 'nested logical filter has no children' };
    }
    for (const child of children as QueryFilter[]) {
      // Children of a nested logical must be true leaves (no further nesting)
      if (isLogicalFilter(child)) {
        return {
          valid: false,
          reason: 'double-nested logical filter not allowed',
        };
      }
      const result = validateLeafFilter(child);
      if (!result.valid) return result;
    }
    return { valid: true };
  }

  if (!Array.isArray(filter.value)) {
    return { valid: true };
  }

  for (const value of filter.value) {
    const result = validateLeafValue(value);
    if (result) continue;

    return {
      valid: false,
      reason: 'leaf filter value is a QueryFilter: ' + value,
    };
  }

  return { valid: true };
};

const validateLeafValue = (
  value:
    | string
    | boolean
    | number
    | QueryFilter
    | (string | number | boolean | QueryFilter)[]
): boolean => {
  if (Array.isArray(value)) {
    for (const subValue of value) {
      const valid = validateLeafValue(subValue);
      if (!valid) {
        return valid;
      }
    }

    return true;
  }

  if (typeof value === 'string') {
    return true;
  }
  if (typeof value === 'number') {
    return true;
  }
  if (typeof value === 'boolean') {
    return true;
  }

  return false;
};

const isLogicalWithChildren = (
  filter: QueryFilter
): { valid: boolean; reason?: string } => {
  if (!isLogicalFilter(filter)) {
    return { valid: false, reason: 'filter is not a logical filter' };
  }

  if (!Array.isArray(filter.value)) {
    return { valid: false, reason: 'filter is not an array' };
  }

  if (filter.value.length < 0) {
    return { valid: false, reason: 'filter has no children' };
  }

  return { valid: true };
};
